import { useCallback, useEffect, useRef, useState } from 'react'
import { createClient, type Client } from './client'
import type {
  Action,
  ChatPayload,
  PlayerId,
  PlayerView,
  RevealPayload,
  RoomInfoPayload,
} from './protocol-types'
import { ConnectionBanner } from './components/ConnectionBanner'
import { JoinScreen } from './screens/JoinScreen'
import { LobbyScreen } from './screens/LobbyScreen'
import { GameScreen } from './screens/GameScreen'
import { RevealScreen } from './screens/RevealScreen'

/**
 * 应用壳。**所有服务端状态都只从推送来**，本地一份都不算。
 *
 * 界面显示的每一样东西 —— 棋盘、剩子数、轮到谁、能点哪 —— 都来自
 * `view` / `roomInfo` / `reveal` 这三份服务端载荷。这里没有任何一处
 * 在浏览器里推算规则，因为客户端一旦自己算，就会出现「界面亮了、
 * 服务端拒了」的分歧，而三个人不能通话，没法互相核对到底谁看错了。
 *
 * 屏幕不存成状态而是**推导**出来的：存了就会有「服务端说 finished、
 * 本地还停在 playing」这种两份真相。
 */
type Screen = 'join' | 'lobby' | 'playing' | 'reveal'

/** 聊天只留最近这些条 —— 一局几十分钟，无上限会一直涨。 */
const CHAT_KEEP = 50

export function App() {
  const clientRef = useRef<Client | null>(null)
  const [conn, setConn] = useState<'connecting' | 'online' | 'offline'>('connecting')
  const [joined, setJoined] = useState<{ code: string; seat: PlayerId } | null>(null)
  const [roomInfo, setRoomInfo] = useState<RoomInfoPayload | null>(null)
  const [view, setView] = useState<PlayerView | null>(null)
  const [reveal, setReveal] = useState<RevealPayload | null>(null)
  const [messages, setMessages] = useState<ChatPayload[]>([])
  const [lobbyError, setLobbyError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    const c = createClient({
      onConnection: setConn,
      onRoomInfo: setRoomInfo,
      onView: (v) => {
        // 新视图到了就说明上一手被服务端接受了，旧的报错该消失。
        setView(v)
        setActionError(null)
        // 收到一份「还没结束」的视图，说明新一局已经开起来了 —— 把上一局的
        // 摊牌清掉，否则屏幕推导会一直停在摊牌屏，新棋盘永远不显示。
        if (!v.ended) setReveal(null)
      },
      onReveal: setReveal,
      onChat: (m) => setMessages((prev) => [...prev, m].slice(-CHAT_KEEP)),
    })
    clientRef.current = c
    return () => {
      clientRef.current = null
      c.close()
    }
  }, [])

  const onJoin = useCallback(
    async (req: { code?: string; displayName: string; token?: string }) => {
      const c = clientRef.current
      if (!c) return
      setPending(true)
      setLobbyError(null)
      try {
        const j = await c.join(req)
        setJoined({ code: j.code, seat: j.seat })
      } catch (err) {
        setLobbyError(err instanceof Error ? err.message : '加入失败')
      } finally {
        setPending(false)
      }
    },
    [],
  )

  const onStart = useCallback(async () => {
    const c = clientRef.current
    if (!c) return
    setPending(true)
    setLobbyError(null)
    try {
      await c.start()
    } catch (err) {
      setLobbyError(err instanceof Error ? err.message : '开局失败')
    } finally {
      setPending(false)
    }
  }, [])

  const onAct = useCallback(async (action: Action) => {
    const c = clientRef.current
    if (!c) return
    try {
      await c.act(action)
    } catch (err) {
      // 服务端已把错误分好类，原样显示。静默吞掉最坏：
      // 玩家点了没反应又不知道为什么，只会反复戳同一个地方。
      setActionError(err instanceof Error ? err.message : '这一手没走成')
    }
  }, [])

  const onChat = useCallback(async (text: string, preset: boolean) => {
    const c = clientRef.current
    if (!c) return
    try {
      await c.chat(text, preset)
    } catch {
      /* 聊天发失败不该打断对局，也不占用报错位（那里留给行动） */
    }
  }, [])

  /**
   * 重开一局 —— 还在原房间，三个人不用重新会合。
   * 走的就是大厅那条 start()，服务端会拦非房主。
   */
  const onRematch = useCallback(async () => {
    const c = clientRef.current
    if (!c) return
    setPending(true)
    setLobbyError(null)
    try {
      await c.start()
      // 不在这里清 reveal：等服务端把新一局的视图推来再清（见 onView）。
      // 先清会让屏幕在新视图到达前闪一下空白的大厅。
    } catch (err) {
      setLobbyError(err instanceof Error ? err.message : '开下一局失败')
    } finally {
      setPending(false)
    }
  }, [])

  /** 退出房间，回进场屏。 */
  const onLeave = useCallback(() => {
    setJoined(null)
    setRoomInfo(null)
    setView(null)
    setReveal(null)
    setMessages([])
    setLobbyError(null)
    setActionError(null)
  }, [])

  const screen: Screen =
    joined === null ? 'join' : reveal !== null ? 'reveal' : view !== null ? 'playing' : 'lobby'

  return (
    <div className="min-h-full">
      <ConnectionBanner state={conn} />
      {screen === 'join' && <JoinScreen onJoin={onJoin} pending={pending} error={lobbyError} />}
      {screen === 'lobby' && joined !== null && (
        <LobbyScreen
          info={roomInfo ?? { code: joined.code, phase: 'waiting', seats: [] }}
          mySeat={joined.seat}
          onStart={onStart}
          pending={pending}
          error={lobbyError}
        />
      )}
      {screen === 'playing' && view !== null && (
        <GameScreen
          view={view}
          messages={messages}
          onAct={onAct}
          onChat={onChat}
          lastError={actionError}
        />
      )}
      {screen === 'reveal' && reveal !== null && joined !== null && (
        <RevealScreen
          reveal={reveal}
          mySeat={joined.seat}
          onRematch={onRematch}
          onLeave={onLeave}
          pending={pending}
          error={lobbyError}
        />
      )}
    </div>
  )
}
